import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { downloadReceipt } from '../utils/CommonFunctions';
import getCurrencySymbol from '../lib/currenyUtils';

/* ─── design tokens (exact Figma specs) ──────────────────────────────────── */
const S = {
  card: {
    marginTop: 40,
    background: '#FFFFFF',
    border: '1px solid #F3F2F2',
    borderRadius: 16,
    paddingTop: 24,
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  heading: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: 18,
    lineHeight: '140%',
    letterSpacing: 0,
    color: '#1D2338',
    margin: 0,
    padding: '0 24px',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#FFFFFF',
    borderBottom: '1px solid #E6E5E5',
    padding: '16px 24px',
    minHeight: 92.5,
    width: '100%',
    boxSizing: 'border-box',
    transition: 'background 0.15s',
  },
  left: { display: 'flex', alignItems: 'center', gap: 16 },
  arrowBoxGreen: {
    width: 40, height: 40, borderRadius: 10,
    background: '#DCFCE7',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  arrowBoxAmber: {
    width: 40, height: 40, borderRadius: 10,
    background: '#FEF3C7',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  meta: { display: 'flex', flexDirection: 'column', gap: 5 },
  name: {
    fontFamily: 'Inter, sans-serif', fontWeight: 600,
    fontSize: 16, lineHeight: '140%', letterSpacing: 0,
    color: '#1D2338', margin: 0,
  },
  chip: {
    display: 'inline-flex', alignItems: 'center',
    background: '#F6F6FB', borderRadius: 4,
    padding: '4px 8px', gap: 10,
    fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#6B7280',
    width: 'fit-content',
  },
  right: { display: 'flex', alignItems: 'center', gap: 12 },
  amountBlock: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 },
  amount: {
    fontFamily: 'Inter, sans-serif', fontWeight: 600,
    fontSize: 16, lineHeight: '140%', letterSpacing: 0,
    color: '#1D2338', margin: 0,
  },
  statusBase: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 109,
    height: 26,
    borderRadius: 6,
    padding: '0 16px',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 600,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
  },
  statusCompleted: { background: '#DCFCE7', color: '#15803D' },
  statusPending: { background: '#FFE2E2', color: '#B91C1C' },
  statusAwaiting: { background: '#FFE5D3', color: '#D97706' },
  sendBtn: {
    fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
    background: '#0B214E', color: '#FFFFFF', border: 'none',
    padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'background 0.2s',
  },
  iconBtn: {
    width: 48, height: 48, borderRadius: 6,
    border: '1px solid #C8CCDA', background: '#FFFFFF',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 8, boxSizing: 'border-box',
    flexShrink: 0, transition: 'background 0.15s',
  },
  viewAllRow: { display: 'flex', justifyContent: 'flex-end', padding: '4px 24px 0' },
  viewAllBtn: {
    background: 'none', border: 'none',
    fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600,
    color: '#2563EB', cursor: 'pointer', textDecoration: 'underline',
  },
  spinner: {
    width: 16, height: 16,
    border: '2px solid #D1D5DB', borderTop: '2px solid #111827',
    borderRadius: '50%', animation: 'txn-spin 0.7s linear infinite',
  },

  arrowBoxAwaiting: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: '#FFE5D3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  arrowBoxPending: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: '#FFE2E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

};

/* ─── SVG icons ──────────────────────────────────────────────────────────── */
const ArrowUpRight = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M4 14L14 4M14 4H7M14 4V11" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
ArrowUpRight.propTypes = {
  color: PropTypes.string.isRequired,
};

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M10 3v9m0 0l-3-3m3 3 3-3M4 15v1a2 2 0 002 2h8a2 2 0 002-2v-1"
      stroke="#6B7280" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"
      stroke="#6B7280" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="10" cy="10" r="2.5" stroke="#6B7280" strokeWidth="1.6" />
  </svg>
);

/* ─── status helpers ─────────────────────────────────────────────────────── */
const checkCompleted = (status) => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'completed' || s === 'complete';
};

const checkAwaiting = (status) =>
  !!status && status.toLowerCase().includes('await');

const getStatusStyle = (status) => {
  if (checkCompleted(status)) return { ...S.statusBase, ...S.statusCompleted };
  if (checkAwaiting(status)) return { ...S.statusBase, ...S.statusAwaiting };
  return { ...S.statusBase, ...S.statusPending };
};

const getStatusLabel = (status) => {
  if (checkCompleted(status)) return 'Completed';
  if (checkAwaiting(status)) return 'Awaiting funds';
  return status || 'Pending';
};

/* ─── component ──────────────────────────────────────────────────────────── */
const TransactionHistory = ({ lastTransactions, onRepeat, buttonLoading }) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [downloadId, setdownloadId] = useState(0);
  const navigate = useNavigate();

  const debitTxns = (lastTransactions || []).filter(
    (txn) => txn?.last_transaction?.type === 'debit'
  );

  console.log('debitTxns', debitTxns);

  if (!debitTxns.length) return null;

  return (
    <>
      <style>{`@keyframes txn-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={S.card}>
        <h2 style={S.heading}>Transactions history</h2>

        {debitTxns.map((txn) => {
          console.log('ravalilll', txn);
          const completed = checkCompleted(txn?.status);
          const isHovered = hoveredId === txn?.id;
          const awaiting = checkAwaiting(txn?.status);
          const meta = txn?.meta_data || {};

          const arrowColor = completed
            ? '#15803D'
            : awaiting
              ? '#D97706'
              : '#B91C1C';

          const arrowBox = completed
            ? S.arrowBoxGreen
            : awaiting
              ? S.arrowBoxAwaiting
              : S.arrowBoxPending;

          return (
            <div
              key={txn.id || txn.date}
              style={{ ...S.row, background: isHovered ? '#F9FAFB' : '#FFFFFF' }}
              onMouseEnter={() => completed && setHoveredId(txn?.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* LEFT */}
              <div style={S.left}>
                <div style={arrowBox}>
                  <ArrowUpRight color={arrowColor} />
                </div>

                <div style={S.meta}>
                  <p style={S.name}>{txn.name}</p>
                  <span style={S.chip}>
                    {new Date(txn.date).toLocaleString()}
                  </span>
                  <span style={S.chip}>
                    ID: {txn?.id || 'TXNBT...'}
                  </span>
                </div>
              </div>

              {/* RIGHT */}
              <div style={S.right}>
                {isHovered && completed ? (
                  <button
                    type="button"
                    onClick={() => onRepeat(txn)}
                    disabled={buttonLoading}
                    style={{
                      ...S.sendBtn,
                      opacity: buttonLoading ? 0.7 : 1,
                      cursor: buttonLoading ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!buttonLoading) e.currentTarget.style.background = '#163A7A';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#0B214E';
                    }}
                  >
                    {buttonLoading ? 'Loading...' : 'Send again ↗'}
                  </button>
                ) : (
                  <div style={S.amountBlock}>
                    <p style={S.amount}>
                      {parseFloat(txn.amount).toFixed(2)} {txn.currency}
                    </p>
                    <span style={getStatusStyle(txn?.status)}>
                      {getStatusLabel(txn?.status)}
                    </span>
                  </div>
                )}

                {/* Download */}
                <button
                  type="button"
                  aria-label="Download receipt"
                  onClick={() => downloadReceipt(txn?.id, setdownloadId)}
                  style={S.iconBtn}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; }}
                >
                  {downloadId === txn?.id ? (
                    <div style={S.spinner} />
                  ) : (
                    <DownloadIcon />
                  )}
                </button>

                {/* View details */}
                {/* <button
                  type="button"
                  aria-label="View transaction details"
                  onClick={() => navigate('/transactions', { state: { txn } })}
                  style={S.iconBtn}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; }}
                >
                  <EyeIcon />
                </button> */}

                <button
                  type="button"
                  onClick={() => {
                    const dateObj = new Date(txn.date);
                    const formatteddate = [
                      String(dateObj.getDate()).padStart(2, '0'),
                      String(dateObj.getMonth() + 1).padStart(2, '0'),
                      dateObj.getFullYear(),
                    ].join('-');
                    const formattedtime = [
                      String(dateObj.getHours()).padStart(2, '0'),
                      String(dateObj.getMinutes()).padStart(2, '0'),
                      String(dateObj.getSeconds()).padStart(2, '0'),
                    ].join(':');

                    const transactionDetails = {
                      id: txn.id,
                      beneName: txn.name,
                      date: `${formatteddate} ${formattedtime}`,
                      transactionType: txn?.last_transaction?.type,
                      debittransactionId: txn.id,
                      createdat: txn?.last_transaction?.created_at ?? txn.date,
                      updatedat: txn?.last_transaction?.updated_at ?? txn.date,
                      sellAmount: `${getCurrencySymbol(txn.currency)} ${txn.amount}`,
                      send_MoneySendAmount: `${getCurrencySymbol(meta.exchange_currency || txn.currency)} ${meta.recipient_amount ?? txn.amount}`,
                      currency: txn.currency,
                      status: txn.status,
                      reason:
                        meta?.reason || meta?.conversion_meta?.reason || '',
                      reference: meta?.reference ?? '',
                      cardFees: txn?.cardFees ?? '',
                      beneficiaryName:
                        meta?.second_beneficiary_name ?? txn.name,
                      beneBankNumber:
                        meta?.second_beneficiary_bank_account_number ||
                        meta?.second_beneficiary_bank_iban ||
                        '',
                      code:
                        meta?.second_beneficiary_bank_code ||
                        meta?.second_beneficiary_bank_bic ||
                        meta?.second_beneficiary_bank_bsb ||
                        meta?.second_beneficiary_bank_aba ||
                        '',
                      senderName: meta?.sender_name ?? '',
                      beneBankType: meta?.type ?? '',
                      beneficiarybankType: meta?.type ?? '',
                      creditAmount: meta?.recipient_amount ?? txn.amount,
                      transactionIds: txn?.transactionIds ?? [],
                      rawMeta: meta,
                    };

                    navigate('/TransactionListDetails', {
                      state: {
                        txn,
                        formatteddate,
                        formattedtime,
                        transactionDetails,
                      },
                    });
                  }}
                  style={S.iconBtn}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                  }}
                >
                  <EyeIcon />
                </button>
              </div>
            </div>
          );
        })}

        <div style={S.viewAllRow}>
          <button style={S.viewAllBtn} onClick={() => navigate('/transactions')}>
            View all
          </button>
        </div>
      </div>
    </>
  );
};

TransactionHistory.propTypes = {
  lastTransactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      currency: PropTypes.string,
      date: PropTypes.string,
      status: PropTypes.string,
      last_transaction: PropTypes.shape({ type: PropTypes.string }),
    })
  ).isRequired,
  onRepeat: PropTypes.func.isRequired,
  buttonLoading: PropTypes.bool,
};

TransactionHistory.defaultProps = {
  lastTransactions: [],
  buttonLoading: false,
};

export default TransactionHistory;
